import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Box, Button, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import React from 'react';

import DragDropZone from '~/components/DragDropZone';
import { BUTTON_SX, SURFACE_RADIUS } from '~/theme';

import { formatFileSize, MAX_FILE_SIZE_MB, type UseDocumentUpload } from './useDocumentUpload';

/**
 * Drop zone + selected-file list for the AI pipeline. Multiple PDFs can be
 * dropped at once; each becomes its own upload. Every row carries the one
 * per-file choice the pipeline can't infer — "Keep as one document", which
 * skips splitting and keeps the whole PDF as a single document.
 *
 * showKeepWhole turns that checkbox off for hosts where the choice has already
 * been made for the operator. An onboarding intake upload is always a contract,
 * so useDocumentUpload sends documentTypeHint: Contract and never reads
 * keepWhole — leaving the checkbox visible there offers a control that does
 * nothing.
 */
const DocumentUploadFileList: React.FC<{
  upload: UseDocumentUpload;
  showKeepWhole?: boolean;
}> = ({ upload, showKeepWhole = true }) => {
  const {
    fileEntries,
    handleFilesSelected,
    handleRemoveFile,
    toggleFileKeepWhole,
    setAllKeepWhole,
  } = upload;
  const keptWholeCount = fileEntries.filter((entry) => entry.keepWhole).length;
  const allKeptWhole = fileEntries.length > 0 && keptWholeCount === fileEntries.length;

  return (
    <>
      <DragDropZone
        acceptedFileTypes={{ 'application/pdf': ['.pdf'] }}
        acceptedFileTypesLabel="PDF"
        fileSizeLimit={MAX_FILE_SIZE_MB}
        onFileSelected={handleFilesSelected}
        multiple
      />

      {fileEntries.length > 0 ? (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {showKeepWhole && fileEntries.length > 1 ? (
            // Row padding + border, so the checkbox lines up with the per-file ones.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 'calc(8px + 1px)' }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {fileEntries.length} files
              </Typography>
              <FormControlLabel
                sx={{ mr: 0, flexShrink: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={allKeptWhole}
                    indeterminate={keptWholeCount > 0 && !allKeptWhole}
                    onChange={() => setAllKeepWhole(!allKeptWhole)}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Keep all as one document
                  </Typography>
                }
              />
              {/* Takes the Remove button's width so the columns align. */}
              <Button
                size="small"
                sx={{ ...BUTTON_SX, visibility: 'hidden' }}
                tabIndex={-1}
                aria-hidden
              >
                Remove
              </Button>
            </Box>
          ) : null}
          {fileEntries.map((entry, index) => (
            <Box
              key={`${entry.file.name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                // The panel radius, matching the drop zone the row appears under.
                borderRadius: `${SURFACE_RADIUS}px`,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <InsertDriveFileOutlinedIcon color="primary" fontSize="small" />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap title={entry.file.name}>
                  {entry.file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(entry.file.size)}
                </Typography>
              </Box>
              {showKeepWhole ? (
                <Tooltip
                  describeChild
                  title="Keep the whole PDF as a single document — use for multi-page files (like a contract) the splitter would otherwise break into pieces. The type is still detected automatically."
                >
                  <FormControlLabel
                    sx={{ mr: 0, flexShrink: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={entry.keepWhole}
                        onChange={() => toggleFileKeepWhole(index)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Keep as one document
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          (skip splitting)
                        </Typography>
                      </Box>
                    }
                  />
                </Tooltip>
              ) : null}
              <Button size="small" onClick={() => handleRemoveFile(index)} sx={BUTTON_SX}>
                Remove
              </Button>
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  );
};

export default DocumentUploadFileList;
